/**
 * AB实验模块自定义 Hooks
 */
import { useEffect, useState, useMemo } from 'react';
import { getAbConfig } from '../service';
import {
  IAbConfigData,
  IUseAbConfigOptions,
  IAbConfigResponse,
} from './types';
import {
  convertEventFiltersToEventList,
  convertFilterToColumnInfo,
  convertMetricToColumnInfo,
  convertDimensionToColumnInfo,
  convertFiltersToOptions,
  convertMetricsToOptions,
  convertDimensionsToOptions,
  toCascaderData,
  isApplicableByUserType,
} from './utils';

/**
 * 获取AB实验配置数据的 Hook
 * @param options - 配置选项
 * @returns 配置数据和加载状态
 */
export function useAbConfig(options: IUseAbConfigOptions = {}) {
  const { gameCode, userType, enabled = true } = options;
  // 保存原始接口数据
  const [rawConfigData, setRawConfigData] = useState<IAbConfigResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled || !gameCode) {
      // 如果未启用或缺少必要参数，不设置数据
      setRawConfigData(null);
      return;
    }

    setLoading(true);
    setError(null);

    const params = {
      game_code: gameCode,
    };
    getAbConfig(params)
      .then((response: any) => {
        setRawConfigData(response as IAbConfigResponse);
      })
      .catch((err: any) => {
        console.error('获取AB实验配置失败:', err);
        setError(err);
        // 失败时不设置数据，返回空数据
        setRawConfigData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [gameCode, enabled]);

  // 使用 useMemo 转换数据
  const configData = useMemo((): IAbConfigData => {
    // 如果没有原始数据，返回空数据
    if (!rawConfigData) {
      return {
        eventList: [],
        allMetrics: [],
        optionMetrics: [],
        allDimensions: [],
        optionDimensions: [],
        allFilters: [],
        optionFilters: [],
        experimentFilters: [],
      };
    }

    // 转换事件筛选器
    const eventList = convertEventFiltersToEventList(rawConfigData.event_filters || []);

    // 转换筛选器（根据用户类型过滤）
    const filteredFilters = (rawConfigData.filters || []).filter((filter) =>
      isApplicableByUserType(filter.applicable_user_types, userType)
    );
    const allFilters = filteredFilters.map((filter, index) =>
      convertFilterToColumnInfo(filter, index)
    );

    const allMetricsData = (rawConfigData.metrics || []).filter((metric) => !metric.is_custom);
    const allCustomMetrics = (rawConfigData.metrics || []).filter((metric) => metric.is_custom);

    // 自定义指标下拉选
    const optionCustomMetrics = (allCustomMetrics || []).map((m) => ({
      value: m?.mapping_id ?? '',
      textEn: m?.display_name_en ?? '',
      textCn: m?.display_name_cn ?? '',
    }));

    // 转换指标（根据用户类型过滤）
    const filteredMetricsData = (allMetricsData || []).filter((metric) =>
      isApplicableByUserType(metric.applicable_user_types, userType)
    );
    const allMetrics = filteredMetricsData.map((metric, index) =>
      convertMetricToColumnInfo(metric, index)
    );

    // 转换维度
    const allDimensions = (rawConfigData.dimensions || []).map((dimension, index) =>
      convertDimensionToColumnInfo(dimension, index)
    );

    // 转换选项数据（按分组，筛选器使用已按 userType 过滤后的列表）
    const optionFilters = convertFiltersToOptions(filteredFilters);
    const optionMetrics = convertMetricsToOptions(filteredMetricsData);
    const optionDimensions = convertDimensionsToOptions(rawConfigData.dimensions || []);
    const experimentFilters = toCascaderData(rawConfigData.experiment_filters || []);
    return {
      eventList,
      allMetrics,
      optionMetrics,
      allDimensions,
      optionDimensions,
      allFilters,
      optionFilters,
      experimentFilters,
      allCustomMetrics,
      optionCustomMetrics,
    };
  }, [rawConfigData, userType]);

  // 返回转换后的数据和原始数据
  return {
    ...(configData || {}),
    loading,
    error,
    rawData: rawConfigData, // 可选：如果需要访问原始数据
  };
}
